import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Fab,
  Paper,
  TextField,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  MusicNote as MusicNoteIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import openAIService from "../../services/openai-service";
import chatSignalRService from "../../services/chat-signalr-service";
import RackStarLogoLight from "../../logos/RackStarLogo3.png";
import RackStarLogoDark from "../../logos/RackStarLogo2.png";
import { useTheme } from "@mui/material/styles";
import FullPageLoader from "../../common/components/FullPageLoader";
import TypingIndicator from "./TypingIndicator";
import { appInsights } from "../../appInsights";
import { useMsal } from "@azure/msal-react";
import axiosIns from "../../common/interceptor/axios-interceptor";

// Message ID generator for stable React keys
let messageIdCounter = 0;
const generateMessageId = () => `msg-${++messageIdCounter}`;

const ChatContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: "350px",
  height: "500px",
  display: "flex",
  flexDirection: "column",
  zIndex: 1000,
  boxShadow: theme.shadows[4],
  borderRadius: "12px",
  transformOrigin: "bottom right",
  [theme.breakpoints.down("sm")]: {
    width: "calc(100vw - 40px)",
    height: "calc(100vh - 100px)",
    bottom: 0,
    right: 0,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: "4px",
  cursor: "ew-resize",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    width: "4px",
    height: "4px",
    backgroundColor: theme.palette.action.hover,
    cursor: "nwse-resize",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "4px",
    height: "4px",
    backgroundColor: theme.palette.action.hover,
    cursor: "nesw-resize",
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  "& .header-content": {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(2),
  },
  "& .logo": {
    height: "24px",
    width: "auto",
    marginRight: theme.spacing(1),
    verticalAlign: "baseline",
    display: "inline-block",
  },
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: theme.spacing(1),
}));

const MessageBubble = styled(Box)(({ theme, isUser, isGreeting = false }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  maxWidth: "80%",
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  color: isUser
    ? theme.palette.primary.contrastText
    : isGreeting ? theme.palette.text.primary : "black",
  alignSelf: isUser ? "flex-end" : "flex-start",
}));

const AIChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 350, height: 500 });
  const [isResizing, setIsResizing] = useState(false);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);
  const [connectionId, setConnectionId] = useState(null);
  const [useStreaming, setUseStreaming] = useState(true);

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamingMessageRef = useRef(""); // Track streaming message without re-renders
  const streamingTimeoutRef = useRef(null); // Timeout safety for streaming flow
  const streamingInactivityTimeoutRef = useRef(null); // Inactivity timeout for stalled streaming
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.username || accounts?.[0]?.name || "unknown";
  const riffAudioRef = useRef(null);
  const userNameRef = useRef(userName); // Ref to track latest userName for timeout callbacks

  // Keep userNameRef in sync with userName to avoid stale closures
  useEffect(() => {
    userNameRef.current = userName;
  }, [userName]);

  // Streaming timeout duration in milliseconds (60 seconds)
  const STREAMING_TIMEOUT_MS = 60000;
  // Inactivity timeout duration in milliseconds (12 seconds) - triggers if no tokens arrive
  const STREAMING_INACTIVITY_TIMEOUT_MS = 12000;

  // Helper to clear the streaming timeout safely (clears both main and inactivity timeouts)
  const clearStreamingTimeout = () => {
    if (streamingTimeoutRef.current) {
      clearTimeout(streamingTimeoutRef.current);
      streamingTimeoutRef.current = null;
    }
    if (streamingInactivityTimeoutRef.current) {
      clearTimeout(streamingInactivityTimeoutRef.current);
      streamingInactivityTimeoutRef.current = null;
    }
  };

  // Helper to start/reset the inactivity timeout
  // Called when streaming starts and whenever a token is received
  const resetInactivityTimeout = () => {
    if (streamingInactivityTimeoutRef.current) {
      clearTimeout(streamingInactivityTimeoutRef.current);
    }
    streamingInactivityTimeoutRef.current = setTimeout(() => {
      appInsights.trackEvent({
        name: "AIChatBot_StreamingInactivityTimeout",
        properties: {
          user: userNameRef.current,
          partialMessage: streamingMessageRef.current?.substring(0, 100) || "",
          timestamp_utc: new Date().toISOString(),
        },
      });
      // Use the same cleanup as handleStreamingTimeout
      handleStreamingTimeout();
    }, STREAMING_INACTIVITY_TIMEOUT_MS);
  };

  // Helper to handle streaming timeout - resets all streaming state
  // Uses userNameRef.current to avoid stale closure when timeout fires
  const handleStreamingTimeout = () => {
    // Clear any pending timeouts first to prevent double invocation
    // (e.g., when inactivity timeout fires, also cancel the main timeout)
    clearStreamingTimeout();

    appInsights.trackEvent({
      name: "AIChatBot_StreamingTimeout",
      properties: {
        user: userNameRef.current,
        partialMessage: streamingMessageRef.current?.substring(0, 100) || "",
        timestamp_utc: new Date().toISOString(),
      },
    });

    const partialContent = streamingMessageRef.current;
    streamingMessageRef.current = "";
    setStreamingMessage("");
    setIsStreaming(false);
    setIsLoading(false);
    setStatusMessage(null);

    // Convert any streaming placeholder to a non-streaming message
    setMessages((prev) => {
      const hasStreamingMessage = prev.some((m) => m.isStreaming);
      if (hasStreamingMessage) {
        return [
          ...prev.filter((m) => !m.isStreaming),
          {
            id: generateMessageId(),
            text: partialContent || "The response timed out. Please try again.",
            isUser: false,
          },
        ];
      }
      return prev;
    });
  };

  // Extract first name from user account
  const getFirstName = () => {
    if (accounts && accounts[0]) {
      // Try givenName first
      if (accounts[0].idTokenClaims?.given_name) {
        return accounts[0].idTokenClaims.given_name;
      }
      // Try extracting from displayName
      if (accounts[0].name) {
        return accounts[0].name.split(" ")[0];
      }
      // Try extracting from username (email)
      if (accounts[0].username) {
        return accounts[0].username.split("@")[0].split(".")[0];
      }
    }
    return "there";
  };

  // Create personalized greeting message
  const createGreetingMessage = useCallback(() => {
    const firstName = getFirstName();
    return {
      id: generateMessageId(),
      text: `Hi ${firstName}! 👋 I'm Riff 🎸, your assistant. I'm here to help you with:

📚 **Documentation & Guides**
• Component usage and integration
• API endpoints and functionality  
• System configuration help
• User guides and tutorials

📧 **Smart Email Assistant**
• Send emails by just mentioning names: "Email John about the pricing update"
• I'll look up email addresses automatically
• Emails are sent as YOU, not as a bot

💡 **Quick Examples:**
• "How do I set up allocations?"
• "Show me the tax calculation API"
• "Email Sarah about the new features"
• "Send the outage report to the team"

Feel free to ask me anything about RackStar!`,
      isUser: false,
      isGreeting: true,
    };
  }, [accounts]);

  // Initialize messages with greeting
  const [messages, setMessages] = useState([createGreetingMessage()]);

  // Preload riff audio
  useEffect(() => {
    try {
      const audio = new Audio("/audio/guitar-riff.wav");
      audio.preload = "auto";
      riffAudioRef.current = audio;
    } catch (_) {
      riffAudioRef.current = null;
    }
  }, []);

  // Initialize SignalR for streaming chat
  useEffect(() => {
    if (!useStreaming) return;

    let isMounted = true;

    const initSignalR = async () => {
      try {
        // Reset retry counter to allow fresh connection attempts
        chatSignalRService.resetRetryCounter();
        const connId = await chatSignalRService.startConnection();
        if (isMounted) {
          setConnectionId(connId);
          // Connection successful - streaming is available
          appInsights.trackEvent({
            name: "AIChatBot_SignalRConnected",
            properties: {
              user: userName,
              connectionId: connId,
              timestamp_utc: new Date().toISOString(),
            },
          });
        }
      } catch (error) {
        console.warn("SignalR connection failed, falling back to REST:", error);
        appInsights.trackException({
          exception: error,
          properties: {
            component: "AIChatBot",
            action: "SignalR_InitialConnection",
            user: userName,
          },
        });
        // Don't permanently disable streaming - just clear connectionId
        // This allows handleSend to attempt reconnection or use REST fallback
        if (isMounted) {
          setConnectionId(null);
        }
      }
    };

    initSignalR();

    // Register SignalR event handlers
    const handleTypingIndicator = (data) => {
      setIsStreaming(data.isTyping);
      if (!data.isTyping) {
        setStatusMessage(null);
      }
    };

    const handleStatusUpdate = (data) => {
      setStatusMessage(data.status);
    };

    const handleTokenChunk = (data) => {
      // Reset inactivity timeout on every token received (including isComplete)
      resetInactivityTimeout();

      if (data.isComplete) {
        // Clear the streaming timeout on successful completion
        clearStreamingTimeout();
        
        // CRITICAL: Capture the final message BEFORE clearing the ref
        // React 18 batches state updates, so setMessages callback may run after
        // streamingMessageRef.current is cleared if we don't capture it first
        const finalMessage = streamingMessageRef.current;
        
        // Clear streaming state immediately to prevent race conditions
        streamingMessageRef.current = "";
        setStreamingMessage("");
        setIsStreaming(false);
        setIsLoading(false);
        setStatusMessage(null);
        
        // Finalize the streaming message - find the most recent streaming message
        // regardless of position (in case other messages were appended during streaming)
        setMessages((prev) => {
          // Find the last message with isStreaming === true
          let streamingIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].isStreaming) {
              streamingIndex = i;
              break;
            }
          }
          if (streamingIndex !== -1) {
            const updated = [...prev];
            updated[streamingIndex] = { 
              id: prev[streamingIndex].id, 
              text: finalMessage, // Use captured value, not the ref
              isUser: false 
            };
            return updated;
          }
          return prev;
        });
      } else {
        // Append to streaming message
        streamingMessageRef.current += data.text;
        setStreamingMessage(streamingMessageRef.current);
      }
    };

    const handleChatError = (data) => {
      // Clear the streaming timeout on error
      clearStreamingTimeout();
      setIsStreaming(false);
      setIsLoading(false);
      setStatusMessage(null);
      setMessages((prev) => [
        ...prev.filter((m) => !m.isStreaming),
        { id: generateMessageId(), text: data.error || "An error occurred. Please try again.", isUser: false },
      ]);
    };

    // Handle disconnection - clear connectionId to allow REST fallback
    const handleDisconnected = (error) => {
      console.warn("SignalR disconnected:", error);
      appInsights.trackEvent({
        name: "AIChatBot_SignalRDisconnected",
        properties: {
          user: userName,
          error: error?.message || String(error),
          timestamp_utc: new Date().toISOString(),
        },
      });
      if (isMounted) {
        setConnectionId(null);
      }
    };

    // Handle reconnection - restore connectionId
    const handleConnected = (connId) => {
      appInsights.trackEvent({
        name: "AIChatBot_SignalRReconnected",
        properties: {
          user: userName,
          connectionId: connId,
          timestamp_utc: new Date().toISOString(),
        },
      });
      if (isMounted) {
        setConnectionId(connId);
      }
    };

    chatSignalRService.onTypingIndicator(handleTypingIndicator);
    chatSignalRService.onStatusUpdate(handleStatusUpdate);
    chatSignalRService.onTokenChunk(handleTokenChunk);
    chatSignalRService.onChatError(handleChatError);
    chatSignalRService.onDisconnected(handleDisconnected);
    chatSignalRService.onConnected(handleConnected);

    return () => {
      isMounted = false;
      // Clear any pending streaming timeout
      clearStreamingTimeout();
      // Remove all callbacks first
      chatSignalRService.removeCallback("onTypingIndicator", handleTypingIndicator);
      chatSignalRService.removeCallback("onStatusUpdate", handleStatusUpdate);
      chatSignalRService.removeCallback("onTokenChunk", handleTokenChunk);
      chatSignalRService.removeCallback("onChatError", handleChatError);
      chatSignalRService.removeCallback("onDisconnected", handleDisconnected);
      chatSignalRService.removeCallback("onConnected", handleConnected);
      // Explicitly stop the SignalR connection
      chatSignalRService.stopConnection();
      // Clear connection state
      setConnectionId(null);
    };
  }, [useStreaming, userName]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, streamingMessage, isStreaming]);

  // Scroll to bottom when chat opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the chat window is fully rendered
      setTimeout(scrollToBottom, 100);
    }
  }, [isOpen]);

  // Update greeting when account information becomes available
  // NOTE: messages is intentionally NOT in the dependency array to prevent infinite loops.
  // The effect should only run when accounts changes, not when messages changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      accounts &&
      accounts[0] &&
      messages.length === 1 &&
      messages[0].isGreeting
    ) {
      setMessages([createGreetingMessage()]);
    }
  }, [accounts, createGreetingMessage]);

  // Handle click outside to close chat
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Check if the click was on the Fab button (we don't want to close if opening)
        const fabButton = event.target.closest('[aria-label="chat"]');
        if (!fabButton) {
          setIsOpen(false);
          appInsights.trackEvent({
            name: `AIChatBot_ClosedByClickOutside`,
            properties: {
              user: userName,
              timestamp_utc: new Date().toISOString(),
            },
          });
        }
      }
    };

    // Add event listener with slight delay to avoid immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, userName]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = dimensions.width;
    const startHeight = dimensions.height;
    const isCorner =
      e.target === e.currentTarget.querySelector("::before") ||
      e.target === e.currentTarget.querySelector("::after");

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const deltaY = isCorner ? e.clientY - startY : 0;

      const newWidth = Math.max(300, startWidth + deltaX);
      const newHeight = isCorner
        ? Math.max(400, startHeight + deltaY)
        : startHeight;

      setDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    // Capture the user message before clearing the input field
    const userMessage = inputValue.trim();

    // Telemetry for user message (log metadata only, no raw content for privacy)
    appInsights.trackEvent({
      name: `AIChatBot_UserMessageSent`,
      properties: {
        user: userName,
        message_length: userMessage.length,
        message_preview: userMessage.slice(0, 100),
        timestamp_utc: new Date().toISOString(),
        timestamp_local: new Date().toLocaleString(),
      },
    });

    // Add user message
    const newMessages = [...messages, { id: generateMessageId(), text: userMessage, isUser: true }];
    setMessages(newMessages);
    setInputValue("");
    setIsLoading(true);

    // Local intent: ask Riff to play a riff
    const normalized = userMessage.toLowerCase();
    const asksToPlayRiff =
      normalized.includes("play me something") ||
      normalized.includes("play something") ||
      (normalized.includes("play") && normalized.includes("riff")) ||
      normalized === "hey riff" ||
      normalized === "riff";

    if (asksToPlayRiff) {
      try {
        await riffAudioRef.current?.play();
        appInsights.trackEvent({
          name: `AIChatBot_RiffAudioPlayed`,
          properties: {
            user: userName,
            source: "local_intent",
            timestamp_utc: new Date().toISOString(),
          },
        });
        setMessages([
          ...newMessages,
          { id: generateMessageId(), text: "Playing your riff 🎸", isUser: false },
        ]);
      } catch (error) {
        setMessages([
          ...newMessages,
          {
            id: generateMessageId(),
            text: "I couldn't play the audio. Check that the riff file exists and your browser allows sound.",
            isUser: false,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Check if streaming is available - try to reconnect if connection was lost
    let effectiveConnectionId = connectionId;
    if (useStreaming && !effectiveConnectionId) {
      // Attempt to reconnect SignalR for streaming
      try {
        chatSignalRService.resetRetryCounter();
        effectiveConnectionId = await chatSignalRService.startConnection();
        setConnectionId(effectiveConnectionId);
        appInsights.trackEvent({
          name: "AIChatBot_SignalRReconnectOnSend",
          properties: {
            user: userName,
            connectionId: effectiveConnectionId,
            timestamp_utc: new Date().toISOString(),
          },
        });
      } catch (reconnectError) {
        console.warn("Could not re-establish SignalR connection, using REST fallback:", reconnectError);
        appInsights.trackEvent({
          name: "AIChatBot_SignalRReconnectFailed",
          properties: {
            user: userName,
            error: reconnectError?.message || String(reconnectError),
            timestamp_utc: new Date().toISOString(),
          },
        });
        // Fall through to REST mode for this message, but don't disable streaming permanently
        effectiveConnectionId = null;
      }
    }

    if (useStreaming && effectiveConnectionId) {
      // Streaming mode via SignalR
      setIsStreaming(true);
      streamingMessageRef.current = "";
      setStreamingMessage("");
      setStatusMessage("Starting...");

      // Add placeholder for streaming message
      setMessages([...newMessages, { id: generateMessageId(), text: "", isUser: false, isStreaming: true }]);

      try {
        // Clear any existing streaming timeout before starting a new one
        clearStreamingTimeout();

        // Set timeout safety BEFORE the POST request - covers both slow POST and slow streaming
        // SignalR completion handlers will clear this timeout on normal completion
        streamingTimeoutRef.current = setTimeout(() => {
          handleStreamingTimeout();
        }, STREAMING_TIMEOUT_MS);

        // Start inactivity timeout - will be reset on each token received
        // Catches case where POST succeeds but no tokens arrive or streaming stalls
        resetInactivityTimeout();

        await axiosIns.post(
          "openai/chat/stream",
          {
            message: userMessage,
            conversationHistory: messages
              .filter((m) => !m.isGreeting && !m.isStreaming)
              .map((m) => ({ text: m.text || m.content || "", isUser: !!m.isUser })),
            connectionId: effectiveConnectionId,
          },
          {
            // Request timeout as additional safety - fails fast if server doesn't respond
            timeout: 30000, // 30 seconds for the POST request itself
          }
        );
      } catch (error) {
        // Clear the streaming timeout on error
        clearStreamingTimeout();
        console.error("Streaming request failed:", error);
        appInsights.trackException({
          exception: error,
          properties: {
            component: "AIChatBot",
            action: "StreamingRequest",
            user: userName,
          },
        });
        // Fully reset streaming state and remove the placeholder message
        streamingMessageRef.current = "";
        setStreamingMessage("");
        setIsStreaming(false);
        setStatusMessage(null);
        setIsLoading(false);
        // Remove any streaming placeholder messages and add error message
        setMessages((prevMessages) => [
          ...prevMessages.filter((m) => !m.isStreaming),
          {
            id: generateMessageId(),
            text: "I apologize, but I'm having trouble connecting. Please try again.",
            isUser: false,
          },
        ]);
      }
    } else {
      // Fallback to REST mode
      try {
        const response = await openAIService.sendMessage(userMessage, messages);
        setMessages([...newMessages, { id: generateMessageId(), text: response, isUser: false }]);
      } catch (error) {
        appInsights.trackException({
          exception: error,
          properties: {
            component: "AIChatBot",
            action: "RESTFallback",
            user: userName,
          },
        });
        setMessages([
          ...newMessages,
          {
            id: generateMessageId(),
            text: "I apologize, but I'm having trouble connecting to the AI service. Please try again later.",
            isUser: false,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handlePlayRiffClick = async () => {
    try {
      await riffAudioRef.current?.play();
      appInsights.trackEvent({
        name: `AIChatBot_RiffAudioPlayed`,
        properties: {
          user: userName,
          source: "button",
          timestamp_utc: new Date().toISOString(),
        },
      });
      setMessages((prev) => [
        ...prev,
        { id: generateMessageId(), text: "Playing your riff 🎸", isUser: false },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: generateMessageId(),
          text: "I couldn't play the audio. Check that the riff file exists and your browser allows sound.",
          isUser: false,
        },
      ]);
    }
  };

  return (
    <>
      <Tooltip title="Ask Riff 🎸" placement="left">
        <Fab
          color="primary"
          aria-label="chat"
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            position: "fixed",
            bottom: 10,
            right: 10,
            zIndex: 1000,
            boxShadow: (theme) => theme.shadows[4],
            "&:hover": {
              transform: "scale(1.1)",
              transition: "transform 0.2s ease-in-out",
            },
          }}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              duration: 0.2,
              ease: "easeOut",
            }}
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 1000,
              width: dimensions.width,
              height: dimensions.height,
              transformOrigin: "bottom right",
            }}
          >
            <ChatContainer
              ref={containerRef}
              sx={{
                width: dimensions.width,
                height: dimensions.height,
                transition: isResizing ? "all 0.2s ease-in-out" : "none",
                boxShadow: (theme) => theme.shadows[4],
                transformOrigin: "bottom right",
              }}
            >
              <ChatHeader>
                <Box className="header-content">
                  <img
                    src={isDarkMode ? RackStarLogoDark : RackStarLogoLight}
                    alt="RackStar Logo"
                    className="logo"
                  />
                  <Typography variant="h6"> Assistant</Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setIsOpen(false)}
                  sx={{
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </ChatHeader>

              <ChatMessages>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <MessageBubble
                      isUser={message.isUser}
                      isGreeting={message.isGreeting}
                      sx={
                        message.isGreeting
                          ? {
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
                            border: `1px solid ${theme.palette.primary.main}30`,
                            boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
                          }
                          : {}
                      }
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: "pre-line",
                          fontWeight: message.isGreeting ? 400 : "inherit",
                        }}
                      >
                        {message.isStreaming ? streamingMessage : message.text}
                      </Typography>
                    </MessageBubble>
                  </motion.div>
                ))}

                {/* Typing indicator with status (streaming mode) */}
                {isStreaming && !streamingMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <TypingIndicator status={statusMessage} />
                  </motion.div>
                )}

                {/* Legacy loading indicator (REST fallback mode) */}
                {isLoading && !isStreaming && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <FullPageLoader />
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </ChatMessages>

              <ChatInput>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  size="small"
                  disabled={isLoading || isStreaming}
                  multiline
                  maxRows={4}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <IconButton
                  aria-label="Play riff"
                  onClick={handlePlayRiffClick}
                  disabled={isLoading || isStreaming}
                  sx={{
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.secondary.light,
                      color: (theme) => theme.palette.secondary.contrastText,
                    },
                  }}
                >
                  <MusicNoteIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading || isStreaming}
                  sx={{
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </ChatInput>

              <ResizeHandle onMouseDown={handleResizeStart} />
            </ChatContainer>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatBot;
